import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #isInitialized = false;
  #isUserInteraction = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    requestAnimationFrame(() => {
      this.#isInitialized = true;
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#isInitialized = false;
    this.#isUserInteraction = false;
  }

  #handleToggle = () => {
    if (!this.#isInitialized || !this.#isUserInteraction) {
      this.#isUserInteraction = false;
      return;
    }
    this.#isUserInteraction = false;

    if (this.details.open) {
      this.#scrollToStart();
    } else {
      this.#scrollToCenter();
    }
  };

  #scrollToStart() {
    if (isMobileBreakpoint()) return;

    const stickyContainer = this.closest('.sticky-content--desktop, [style*="sticky"]');
    if (stickyContainer) {
      const style = window.getComputedStyle(stickyContainer);
      if (style.position === 'sticky') {
        const rect = stickyContainer.getBoundingClientRect();
        const topVal = parseFloat(style.top) || 0;
        if (Math.abs(rect.top - topVal) <= 5) return;
      }
    }

    const scrollContainer = this.closest('.drawer__content, .quick-add-modal__content, dialog');
    
    const header = document.querySelector('#header-group, #header-component, .header-wrapper, header');
    let headerHeight = 0;
    if (header) {
      headerHeight = header.offsetHeight;
    }
    const cssHeaderHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    headerHeight = Math.max(headerHeight, cssHeaderHeight);
    const offset = Math.max(headerHeight + 20, 100);

    if (scrollContainer) {
      setTimeout(() => {
        const containerRect = scrollContainer.getBoundingClientRect();
        const rect = this.getBoundingClientRect();
        const targetY = scrollContainer.scrollTop + (rect.top - containerRect.top) - 20;
        scrollContainer.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      }, 150);
    } else {
      setTimeout(() => {
        const rect = this.getBoundingClientRect();
        const targetY = rect.top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }, 150);
    }
  }

  #scrollToCenter() {
    if (isMobileBreakpoint()) return;

    const stickyContainer = this.closest('.sticky-content--desktop, [style*="sticky"]');
    if (stickyContainer) {
      const style = window.getComputedStyle(stickyContainer);
      if (style.position === 'sticky') {
        const rect = stickyContainer.getBoundingClientRect();
        const topVal = parseFloat(style.top) || 0;
        if (Math.abs(rect.top - topVal) <= 5) return;
      }
    }

    const scrollContainer = this.closest('.drawer__content, .quick-add-modal__content, dialog');

    if (scrollContainer) {
      setTimeout(() => {
        const containerRect = scrollContainer.getBoundingClientRect();
        const rect = this.getBoundingClientRect();
        const targetY = scrollContainer.scrollTop + (rect.top - containerRect.top) - (containerRect.height / 2) + (rect.height / 2);
        scrollContainer.scrollTo({
          top: Math.max(0, targetY),
          behavior: 'smooth'
        });
      }, 150);
    } else {
      setTimeout(() => {
        const rect = this.getBoundingClientRect();
        const targetY = rect.top + window.scrollY - (window.innerHeight / 2) + (rect.height / 2);
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }, 150);
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    this.#isUserInteraction = true;
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.#isUserInteraction = true;
    }

    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
